import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Zap, Plus, Send, Trash2, PlayCircle, CheckCircle2, XCircle } from "lucide-react";

export default function WebhookRulesPage() {
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [dispatchLog, setDispatchLog] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, e, l] = await Promise.all([
        api.get("/admin/webhooks/rules"),
        api.get("/admin/webhooks/events-catalog"),
        api.get("/admin/webhooks/events?limit=25"),
      ]);
      setRules(r.data.rules || []);
      setEvents(e.data.events || []);
      setDispatchLog(l.data.events || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await api.delete(`/admin/webhooks/rules/${id}`); toast.success("Rule deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const toggle = async (rule) => {
    try { await api.patch(`/admin/webhooks/rules/${rule.id}`, { enabled: !rule.enabled }); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const test = async (rule) => {
    try {
      const { data } = await api.post(`/admin/webhooks/rules/${rule.id}/test`);
      if (data.status && data.status >= 200 && data.status < 300) {
        toast.success(`Test dispatched — HTTP ${data.status} in ${data.duration_ms}ms`);
      } else {
        toast.error(`Test failed — ${data.error || `HTTP ${data.status}`}`);
      }
      setTimeout(load, 400);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Automation"
        title="Webhook Rules"
        subtitle="Fire signed HTTP POSTs to any URL when a domain event happens. HMAC-SHA256 signatures via optional shared secret."
        actions={<NewRuleDialog events={events} onCreated={load} />}
      />

      <Card className="border-white/10 bg-card p-0 mb-4 overflow-hidden">
        <div className="p-4 border-b border-white/10 overline">Rules ({rules.length})</div>
        {rules.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No rules yet. Create the first one above.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {rules.map((r) => (
              <div key={r.id} className="p-4 flex items-center gap-4" data-testid={`webhook-rule-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{r.name}</span>
                    <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10 text-[10px] uppercase tracking-widest">{r.event}</Badge>
                    {r.enabled ? (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px] uppercase tracking-widest">Enabled</Badge>
                    ) : (
                      <Badge variant="outline" className="border-zinc-500/40 text-zinc-400 bg-zinc-500/10 text-[10px] uppercase tracking-widest">Paused</Badge>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 truncate">{r.url}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
                    {r.delivery_count || 0} dispatches
                    {r.last_status && (
                      <> · last <span className={r.last_status >= 200 && r.last_status < 300 ? "text-emerald-400" : "text-red-400"}>HTTP {r.last_status}</span></>
                    )}
                    {r.property_id ? ` · property ${r.property_id.slice(0, 8)}` : " · portfolio-wide"}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`webhook-toggle-${r.id}`} />
                  <Button size="sm" variant="outline" className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 h-8" onClick={() => test(r)} data-testid={`webhook-test-${r.id}`}>
                    <PlayCircle className="h-3 w-3 mr-1" /> Test
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-300 h-8" data-testid={`webhook-delete-${r.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`webhook-delete-confirm-${r.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete rule &ldquo;{r.name}&rdquo;?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This stops all future <code>{r.event}</code> deliveries to <span className="break-all">{r.url}</span>. Past dispatches remain in the log for audit. You can always create a fresh rule later.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`webhook-delete-cancel-${r.id}`}>Keep it</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(r.id)} className="bg-red-500 hover:bg-red-600" data-testid={`webhook-delete-yes-${r.id}`}>Delete rule</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="border-white/10 bg-card p-0 overflow-hidden">
        <div className="p-4 border-b border-white/10 overline">Recent dispatches</div>
        {dispatchLog.length === 0 ? (
          <div className="p-6 text-center text-zinc-500 text-sm">No dispatches yet — hit Test on any rule.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {dispatchLog.map((e) => (
              <div key={e.id} className="p-3 flex items-center gap-3 text-sm">
                {e.status && e.status >= 200 && e.status < 300 ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    <span className="text-zinc-400">{e.event}</span> → <span>{e.url}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {e.fired_at?.slice(0, 19).replace("T", " ")} · {e.duration_ms}ms
                    {e.error ? <span className="text-red-400"> · {e.error}</span> : (e.status && <> · HTTP {e.status}</>)}
                    {e.test && " · TEST"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function NewRuleDialog({ events, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", event: events[0] || "payment.received",
    url: "", secret: "", enabled: true,
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: "", event: events[0] || "payment.received", url: "", secret: "", enabled: true });
  }, [open, events]);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!/^https?:\/\//i.test(form.url)) return toast.error("URL must start with http(s)://");
    setSaving(true);
    try {
      await api.post("/admin/webhooks/rules", form);
      toast.success("Rule created");
      setOpen(false); onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="webhook-new-rule-btn" className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" /> New rule
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="webhook-new-rule-dialog">
        <DialogHeader>
          <DialogTitle><Zap className="h-4 w-4 inline mr-1 text-orange-400" /> New webhook rule</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            When the event fires we POST a signed JSON body to your URL. Add a secret to sign with HMAC-SHA256.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input data-testid="webhook-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Zapier: post payments to Slack" />
          </div>
          <div>
            <Label>Event</Label>
            <Select value={form.event} onValueChange={(v) => setForm({ ...form, event: v })}>
              <SelectTrigger data-testid="webhook-event" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {events.map((ev) => <SelectItem key={ev} value={ev}>{ev}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Endpoint URL</Label>
            <Input data-testid="webhook-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://hooks.zapier.com/…" />
          </div>
          <div>
            <Label>HMAC secret (optional)</Label>
            <Input data-testid="webhook-secret" type="password" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} placeholder="Adds X-Prestigious-Signature: sha256=<hmac>" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="webhook-save">
            <Send className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Create rule"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
